import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import Typography from "@mui/material/Typography";
import { Avatar, DataRow, ScrollArea } from "@outpave/ds";
import { titleCase } from "title-case";
import dayjs from "utils/dayjs";
import { GET_RECENT_TRANSACTIONS } from "graphql/transaction";
import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import {
  CARD_SPEND_STATUSES,
  LEDGER_TYPES,
} from "utils/transactions/transactionGroups";
import { formatCashFull } from "utils/formatCash";
import { Stack } from "components/layout/reusableLayouts";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import { RenderCompliance } from "components/common/RenderCompliance";
import CustomSkeleton from "components/common/CustomSkeleton";
import { transactionMeta } from "utils/transactions/transactionRow";
import type { TransactionRow } from "utils/transactions/transactionRow";

const RECENT_COUNT = 10;

/** Matches the Avatar `md` diameter, so a logo and a lettermark line up. */
const MARK_SIZE = 28;

interface TransactionNode {
  id: string;
  merchant?: string | null;
  amount?: number | null;
  currency?: string | null;
  merchantData?: {
    url?: string | null;
    logo?: string | null;
    cachedLogo?: string | null;
  } | null;
  user?: { firstName?: string | null; lastName?: string | null } | null;
  transactionDate?: number | null;
  createdAt?: number | null;
  card?: { cardNumber?: string | null } | null;
  status?: TransactionStatus | null;
  policyStatus?: unknown;
  policyStatusDisplay?: unknown;
}

interface Query {
  transactions?: { edges?: { node: TransactionNode }[] | null } | null;
}

interface Group {
  key: string;
  label: string;
  rows: TransactionRow[];
}

/** Pending first as one group, then the rest by date, newest first. */
const groupRows = (rows: TransactionRow[]): Group[] => {
  const pending = rows.filter((r) => r.status === TransactionStatus.PENDING);
  const rest = rows.filter((r) => r.status !== TransactionStatus.PENDING);

  const byDate = new Map<string, TransactionRow[]>();
  rest.forEach((r) => {
    const key = r.date ? dayjs(r.date).format("YYYY-MM-DD") : "";
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key)!.push(r);
  });

  const dated = [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([key, group]) => ({
      key,
      label: key ? dayjs(group[0]!.date).format("MMM D, YYYY") : "Undated",
      rows: group,
    }));

  return pending.length
    ? [{ key: "pending", label: "Pending", rows: pending }, ...dated]
    : dated;
};

export interface RecentTransactionsProps {
  /** "company" is whatever the viewer's role can see, so a /my/ surface must pass "mine". */
  scope: "mine" | "company";
  onRowClick?: (id: string) => void;
  statuses?: TransactionStatus[];
  types?: TransactionsType[];
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  scope,
  onRowClick,
  statuses = CARD_SPEND_STATUSES,
  types = LEDGER_TYPES,
}) => {
  const { t } = useTranslation();
  const showSpender = scope === "company";

  const { data, loading } = useQuery<Query>(GET_RECENT_TRANSACTIONS, {
    variables: {
      first: RECENT_COUNT,
      transactionStatus: statuses,
      type: types,
      mine: scope === "mine",
    },
    fetchPolicy: "cache-and-network",
  });

  const rows: TransactionRow[] = useMemo(
    () =>
      (data?.transactions?.edges ?? []).map(({ node }) => ({
        id: node.id,
        merchant: titleCase(
          (node.merchant || t("Unknown Merchant")).toLowerCase(),
        ),
        spender: [node.user?.firstName, node.user?.lastName]
          .filter(Boolean)
          .join(" "),
        date: node.transactionDate ?? node.createdAt,
        cardNumber: node.card?.cardNumber,
        status: node.status,
        policyStatus: node.policyStatus,
        policyStatusDisplay: node.policyStatusDisplay,
        amount: formatCashFull(node.amount, node.currency),
        url: node.merchantData?.url ?? "",
        logo: node.merchantData?.logo ?? "",
        cachedLogo: node.merchantData?.cachedLogo ?? "",
      })),
    [data, t],
  );

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {loading && rows.length === 0 ? (
        <CustomSkeleton variant="merchant-list" rows={4} />
      ) : rows.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("No transactions found")}
        </Typography>
      ) : (
        <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
          <Stack gap="var(--space-5)">
            {groupRows(rows).map((group) => (
              <Stack key={group.key} gap="var(--space-2)">
                <Typography
                  variant="subtitle2"
                  sx={{ color: "var(--color-text-primary)" }}
                >
                  {t(group.label)}
                </Typography>

                {group.rows.map((row) => {
                  const hasLogo = !!(row.url || row.logo || row.cachedLogo);

                  return (
                    <DataRow
                      key={row.id}
                      type="amount"
                      icon={{
                        glyph: hasLogo ? (
                          <RenderMerchantDisplay
                            merchant={row.merchant}
                            url={row.url ?? ""}
                            logo={row.logo ?? ""}
                            cachedLogo={row.cachedLogo ?? ""}
                            withTitle={false}
                            logoType=""
                            height={MARK_SIZE}
                            width={MARK_SIZE}
                          />
                        ) : (
                          <Avatar
                            name={row.merchant}
                            size="md"
                            variant="rounded"
                          />
                        ),
                      }}
                      title={row.merchant}
                      subtitle={transactionMeta([
                        showSpender ? row.spender : undefined,
                        row.date ? dayjs(row.date).format("MMM D") : undefined,
                        row.cardNumber ? `•• ${row.cardNumber}` : undefined,
                      ])}
                      amount={row.amount}
                      trailing={
                        <RenderCompliance
                          policyStatus={row.policyStatus}
                          policyStatusDisplay={row.policyStatusDisplay}
                        />
                      }
                      onClick={
                        onRowClick ? () => onRowClick(row.id) : undefined
                      }
                    />
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Stack>
  );
};

export default RecentTransactions;
